import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Building2, Users, Plus, ArrowLeft, ArrowRight, Trash2, Pencil, Check, X,
  AlertCircle, Loader2, ShieldCheck, Mail, KeyRound,
} from "lucide-react";
import SEO from "../components/SEO";
import { useProAuth } from "../components/ProAuthContext";
import { UserIdField } from "../components/UserIdField";
import StructureAnnuaireForm from "../components/mss/StructureAnnuaireForm";
import ProsAnnuaireSuggestions, { professionConnue, prenomPropre } from "../components/mss/ProsAnnuaireSuggestions";
import { getIdTypeMeta, validateUserId, formatUserIdLabel, getUserBadge } from "../lib/userIdTypes";
import { API } from "../apiBase";
export default function MssanteStructures() {
  const { user } = useProAuth();
  const navigate = useNavigate();
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [usersByStruct, setUsersByStruct] = useState({});
  const [addStructOpen, setAddStructOpen] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await axios.get(`${API}/pro/structures`);
      setStructures(data || []);
    } catch (e) {
      setErr("Impossible de charger vos structures.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const loadUsers = async (sid) => {
    try {
      const { data } = await axios.get(`${API}/pro/structures/${sid}/users`);
      setUsersByStruct((m) => ({ ...m, [sid]: data || [] }));
    } catch {
      setUsersByStruct((m) => ({ ...m, [sid]: [] }));
    }
  };

  const onOpenStructure = async (sid) => {
    if (openId === sid) { setOpenId(null); return; }
    setOpenId(sid);
    if (!usersByStruct[sid]) await loadUsers(sid);
  };

  const onStructCreated = (s) => {
    setStructures((arr) => [s, ...arr]);
    setAddStructOpen(false);
  };

  const onStructDelete = async (sid, name) => {
    if (!window.confirm(`Supprimer la structure « ${name} » ?\nLes utilisateurs associés seront aussi supprimés (uniquement si aucune messagerie n'y est rattachée).`)) return;
    try {
      await axios.delete(`${API}/pro/structures/${sid}`);
      setStructures((arr) => arr.filter((s) => s.id !== sid));
    } catch (e) {
      alert(e?.response?.data?.detail || "Suppression impossible.");
    }
  };

  const onUserCreated = (sid, u) => {
    setUsersByStruct((m) => ({ ...m, [sid]: [...(m[sid] || []), u] }));
    setStructures((arr) => arr.map((s) => s.id === sid ? { ...s, user_count: (s.user_count || 0) + 1 } : s));
  };
  const onUserDeleted = (sid, uid) => {
    setUsersByStruct((m) => ({ ...m, [sid]: (m[sid] || []).filter((u) => u.id !== uid) }));
    setStructures((arr) => arr.map((s) => s.id === sid ? { ...s, user_count: Math.max(0, (s.user_count || 0) - 1) } : s));
  };
  const onUserUpdated = (sid, u) => {
    setUsersByStruct((m) => ({ ...m, [sid]: (m[sid] || []).map((x) => x.id === u.id ? u : x) }));
  };

  return (
    <div className="bg-[var(--mc-navy-950)] min-h-screen" data-testid="page-mssante-structures">
      <SEO title="Mes structures · MetaConnect" canonical="/mssante/espace/structures" noIndex />

      <section className="hex-grid pt-32 pb-10">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <Link to="/mssante/espace" className="text-[12px] text-[#475569] hover:text-[#0F2B5B] inline-flex items-center gap-1.5">
            <ArrowLeft size={12} /> Retour à mon espace
          </Link>
          <div className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-emerald-600">
            <ShieldCheck size={13} /> Structures & utilisateurs
          </div>
          <div className="mt-3 flex items-end justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl font-semibold leading-tight">
                Mes structures
              </h1>
              <p className="mt-2 text-[13px] text-[#475569]">
                Gérez vos entités juridiques et les utilisateurs rattachés. Ils pourront ensuite être associés aux messageries.
              </p>
            </div>
            <button onClick={() => setAddStructOpen(true)} data-testid="struct-add"
                    className="btn-primary px-5 py-2.5 rounded-full text-[13px] font-medium inline-flex items-center gap-2">
              <Plus size={14} /> Nouvelle structure
            </button>
          </div>
        </div>
      </section>

      <section className="hex-soft py-8 lg:py-10">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          {addStructOpen && (
            <div className="mb-5">
              <NewStructureForm onCancel={() => setAddStructOpen(false)} onCreated={onStructCreated} />
            </div>
          )}

          {loading && <div className="text-[#475569] text-[13px]">Chargement…</div>}
          {err && <div className="text-[#FFB4B4] text-[13px]">{err}</div>}

          {!loading && structures.length === 0 && !addStructOpen && (
            <div className="mc-card p-10 text-center" data-testid="struct-empty">
              <Building2 size={28} className="mx-auto text-[#0369A1]" />
              <h3 className="mt-4 font-display text-[#0F2B5B] text-lg">Aucune structure pour le moment</h3>
              <p className="mt-2 text-[13px] text-[#475569] max-w-md mx-auto">
                Créez une première structure pour démarrer (votre cabinet, votre établissement…).
              </p>
              <button onClick={() => setAddStructOpen(true)}
                      className="mt-5 btn-primary px-6 py-2.5 rounded-full text-[13px] font-medium inline-flex items-center gap-2">
                <Plus size={14} /> Créer ma première structure
              </button>
            </div>
          )}

          <div className="grid gap-3">
            {structures.map((s) => (
              <article key={s.id} className="mc-card overflow-hidden" data-testid={`struct-card-${s.id}`}>
                <button onClick={() => onOpenStructure(s.id)}
                        className="w-full flex items-center gap-4 p-5 text-left hover:bg-[#F8FAFC] transition">
                  <div className="grid place-items-center h-11 w-11 rounded-full bg-[rgba(45,123,255,0.14)] border border-[rgba(45,123,255,0.30)] text-[#0369A1] shrink-0">
                    <Building2 size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[#0F2B5B] font-medium truncate">{s.structure_name}</div>
                    <div className="mt-0.5 flex items-center gap-3 text-[12px] text-[#475569] flex-wrap">
                      <span>{s.structure_id_type} <span className="text-[#0F2B5B]/80 font-mono">{s.structure_id}</span></span>
                      <span>·</span>
                      <span>{s.user_count || 0} utilisateur(s)</span>
                      <span>·</span>
                      <span>{s.mailbox_count || 0} messagerie(s)</span>
                    </div>
                  </div>
                  <div className="text-[#475569]">
                    {openId === s.id ? <X size={14} /> : <ArrowRight size={14} />}
                  </div>
                </button>

                {openId === s.id && (
                  <div className="border-t border-[rgba(127,179,255,0.10)] p-5 bg-[rgba(0,0,0,0.18)] grid gap-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <h3 className="text-[12px] uppercase tracking-[0.22em] text-[#0369A1] inline-flex items-center gap-2">
                        <Users size={13} /> Utilisateurs rattachés
                      </h3>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link to="/mssante/espace/messagerie/nouvelle"
                              className="text-[11px] text-[#0F2B5B] hover:text-[#0F2B5B] inline-flex items-center gap-1">
                          <Mail size={11} /> Créer une messagerie
                        </Link>
                        {s.mailbox_count === 0 && (
                          <button onClick={() => onStructDelete(s.id, s.structure_name)}
                                  data-testid={`struct-delete-${s.id}`}
                                  className="text-[11px] text-[#FFB4B4] hover:text-[#ffd8d8] inline-flex items-center gap-1">
                            <Trash2 size={11} /> Supprimer la structure
                          </button>
                        )}
                      </div>
                    </div>

                    <UsersBlock structureId={s.id}
                                users={usersByStruct[s.id] || []}
                                onAdd={(u) => onUserCreated(s.id, u)}
                                onUpdate={(u) => onUserUpdated(s.id, u)}
                                onDelete={(uid) => onUserDeleted(s.id, uid)} />
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function UsersBlock({ structureId, users, onAdd, onUpdate, onDelete }) {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const onUserDelete = async (uid, name) => {
    if (!window.confirm(`Retirer ${name} de cette structure ?`)) return;
    try {
      await axios.delete(`${API}/pro/structures/${structureId}/users/${uid}`);
      onDelete(uid);
    } catch (e) {
      alert(e?.response?.data?.detail || "Suppression impossible.");
    }
  };

  return (
    <div className="grid gap-2">
      {users.length === 0 && !open && (
        <div className="text-[12.5px] text-[#475569] py-2">
          Aucun utilisateur rattaché. Ajoutez-en un pour pouvoir l'utiliser comme responsable de messagerie.
        </div>
      )}

      {users.map((u) => (
        <div key={u.id} className="rounded-lg border border-[rgba(127,179,255,0.14)] bg-[#F8FAFC] p-3 lg:p-4"
             data-testid={`struct-user-${u.id}`}>
          {editingId === u.id ? (
            <UserEditForm structureId={structureId} initial={u}
                          onCancel={() => setEditingId(null)}
                          onSaved={(nu) => { onUpdate(nu); setEditingId(null); }} />
          ) : (
            <div className="flex items-center gap-3 flex-wrap">
              <div className="grid place-items-center h-9 w-9 rounded-full bg-[#EFF6FF] text-[#0369A1] shrink-0">
                <Users size={13} />
              </div>
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[#0F2B5B] font-medium text-[13.5px]">{u.first_name} {u.last_name}</span>
                  {(() => {
                    const b = getUserBadge(u.id_type);
                    return b ? (
                      <span className={`text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full border ${
                        b.kind === "soignant"
                          ? "border-[rgba(110,231,197,0.4)] text-emerald-600 bg-[rgba(110,231,197,0.06)]"
                          : "border-[rgba(127,179,255,0.32)] text-[#0F2B5B] bg-[#EFF6FF]"
                      }`}>{b.label}</span>
                    ) : null;
                  })()}
                </div>
                <div className="text-[11.5px] text-[#475569] mt-0.5">
                  {u.profession}
                  {u.id_type && u.professional_id && <> · {getIdTypeMeta(u.id_type)?.label || u.id_type} <span className="font-mono">{u.professional_id}</span></>}
                  <span className="mx-2">·</span>{u.email}
                  {u.phone && <> · {u.phone}</>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Link to={`/mssante/espace/utilisateurs/${u.id}/acces`}
                      data-testid={`struct-user-acces-${u.id}`}
                      className="text-[11px] text-emerald-600 hover:text-[#0F2B5B] inline-flex items-center gap-1 px-2 py-1">
                  <KeyRound size={11} /> Droits
                </Link>
                <button onClick={() => setEditingId(u.id)} data-testid={`struct-user-edit-${u.id}`}
                        className="text-[11px] text-[#0F2B5B] hover:text-[#0F2B5B] inline-flex items-center gap-1 px-2 py-1">
                  <Pencil size={11} /> Modifier
                </button>
                <button onClick={() => onUserDelete(u.id, `${u.first_name} ${u.last_name}`)}
                        data-testid={`struct-user-delete-${u.id}`}
                        className="text-[11px] text-[#FFB4B4] hover:text-[#ffd8d8] inline-flex items-center gap-1 px-2 py-1">
                  <Trash2 size={11} /> Retirer
                </button>
              </div>
            </div>
          )}
        </div>
      ))}

      {open ? (
        <UserEditForm structureId={structureId}
                      onCancel={() => setOpen(false)}
                      onSaved={(u) => { onAdd(u); setOpen(false); }} />
      ) : (
        <button onClick={() => setOpen(true)} data-testid={`struct-${structureId}-add-user`}
                className="btn-ghost mt-1 px-4 py-2 rounded-full text-[12px] font-medium inline-flex items-center gap-1.5 self-start">
          <Plus size={13} /> Ajouter un utilisateur
        </button>
      )}
    </div>
  );
}

function UserEditForm({ structureId, initial, onCancel, onSaved }) {
  const isEdit = !!initial;
  const [t, setT] = useState({
    last_name: initial?.last_name || "",
    first_name: initial?.first_name || "",
    email: initial?.email || "",
    phone: initial?.phone || "",
    profession: initial?.profession || "",
    id_type: initial?.id_type || "RPPS",
    professional_id: initial?.professional_id || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const validation = validateUserId(t.id_type, t.professional_id, { initial });
  const idError = submitAttempted && !validation.ok ? validation.error : null;

  const submit = async () => {
    setSubmitAttempted(true);
    setErr("");
    if (!validation.ok) return;
    setBusy(true);
    try {
      const payload = { ...t };
      let res;
      if (isEdit) {
        res = await axios.put(`${API}/pro/structures/${structureId}/users/${initial.id}`, payload);
      } else {
        res = await axios.post(`${API}/pro/structures/${structureId}/users`, payload);
      }
      onSaved(res.data);
    } catch (e) {
      setErr(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Sauvegarde impossible.");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-slate-200 p-4 lg:p-5 bg-[#EFF6FF] grid gap-3">
      <div className="text-[12px] uppercase tracking-[0.16em] text-[#0369A1]">{isEdit ? "Modifier l'utilisateur" : "Nouvel utilisateur"}</div>
{!isEdit && <ProsAnnuaireSuggestions structureId={structureId} onChoisir={(p) => setT((s) => ({ ...s, last_name: p.nom, first_name: p.prenom, profession: p.profession || s.profession, id_type: "RPPS", professional_id: p.rpps }))} />}
      <div className="grid md:grid-cols-2 gap-3">
        <Input label="Nom *" value={t.last_name} onChange={(v) => setT({ ...t, last_name: v })} testid="struct-user-last" />
        <Input label="Prénom *" value={t.first_name} onChange={(v) => setT({ ...t, first_name: v })} testid="struct-user-first" />
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <Input type="email" label="Email *" value={t.email} onChange={(v) => setT({ ...t, email: v })} testid="struct-user-email" />
        <Input label="Téléphone" value={t.phone} onChange={(v) => setT({ ...t, phone: v })} testid="struct-user-phone" />
      </div>
      <Input label="Profession *" value={t.profession} onChange={(v) => setT({ ...t, profession: v })}
             placeholder="ex. Opticien-lunetier" testid="struct-user-profession" />
      <UserIdField idType={t.id_type} value={t.professional_id}
                   onChange={({ id_type, professional_id }) => setT({ ...t, id_type, professional_id })}
                   errorOverride={idError} idPrefix="struct-userid" initial={initial}
 onPraticien={(d) => setT((s) => ({ ...s, last_name: s.last_name || d.nom || "", first_name: s.first_name || prenomPropre(d.prenom), profession: s.profession || professionConnue(d.profession) }))} />
      {err && <Err>{err}</Err>}
      <div className="flex items-center gap-2 justify-end">
        <button onClick={onCancel} className="btn-ghost px-4 py-2 rounded-full text-[12px]">Annuler</button>
        <button onClick={submit}
                disabled={busy || !t.last_name || !t.first_name || !t.email || !t.profession || !t.id_type || !t.professional_id}
                className="btn-primary px-4 py-2 rounded-full text-[12px] font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid="struct-user-submit">
          {busy && <Loader2 size={12} className="animate-spin" />}
          {isEdit ? "Enregistrer" : "Créer"}
        </button>
      </div>
    </div>
  );
}

function NewStructureForm({ onCancel, onCreated }) {
  return <StructureAnnuaireForm onCancel={onCancel} onCreated={onCreated} testid="struct-annu" />;
}

// Atoms
function Lbl({ children }) { return <span className="block text-[11px] uppercase tracking-[0.16em] text-[#0369A1] mb-2">{children}</span>; }
function Err({ children }) { return <p className="mt-1 text-[12px] text-[#FFB4B4] inline-flex items-center gap-1"><AlertCircle size={12} /> {children}</p>; }
function Input({ label, value, onChange, placeholder, type = "text", inputMode, testid }) {
  return (
    <div>
      <Lbl>{label}</Lbl>
      <input type={type} inputMode={inputMode} data-testid={testid}
             className="mc-input w-full" value={value || ""}
             onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}
